import { useState, useEffect } from 'react';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { rshd } from '../../utils/api';
import { ShoppingBag, Search, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import useInventoryUpdates from '../../hooks/useInventoryUpdates';
import ProductCard, { ProductCardGrid, ProductCardSkeleton, EmptyProductState } from './ProductCard';

// DealShaq 20-Category Taxonomy
// DealShaq 20-Category Taxonomy (sorted A-Z, Miscellaneous last)
const CATEGORIES = [
  "Baby & Kids", "Bakery & Bread", "Beverages", "Breakfast & Cereal",
  "Dairy & Eggs", "Deli & Prepared Foods", "Frozen Foods",
  "Fruits", "Health & Nutrition", "Household Essentials",
  "Meat & Poultry", "Oils, Sauces & Spices", "Pantry Staples",
  "Pasta, Rice & Grains", "Personal Care", "Pet Supplies",
  "Seafood", "Snacks & Candy", "Vegetables", "Miscellaneous"
];

export default function ConsumerBrowse({ user, onLogout }) {
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Real-time inventory updates
  const token = localStorage.getItem('token');
  const { getItemStatus, hasRecentUpdate, isConnected } = useInventoryUpdates(token);

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    filterDeals();
  }, [deals, selectedCategory, searchQuery]);

  const loadDeals = async () => {
    try {
      const response = await rshd.list();
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const filterDeals = () => {
    let filtered = deals;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((d) => d.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (d) =>
          (d.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (d.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredDeals(filtered);
  };

  const addToCart = (deal) => {
    const existingItem = cart.find((item) => item.rshd_id === deal.id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.rshd_id === deal.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setCart([...cart, { 
        rshd_id: deal.id, 
        name: deal.name, 
        price: deal.deal_price, 
        quantity: 1,
        regular_price: deal.regular_price || deal.deal_price * 2,  // Include for charity calculation
        discount_level: deal.discount_level || 1  // Include for charity calculation
      }]);
    }
    toast.success(`${deal.name} added to cart`);
  };

  const goToCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.location.href = '/consumer/checkout';
  };

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Surplus-Based Discount-Optimized Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Surplus-Based Discount-Optimized Model</h4>
                <p className="text-sm text-gray-600">
                  These deals were posted by local retailers with urgent items that must move fast. 
                  You're seeing them because they match your Favorites List favorites. 
                  No searching needed - deals come to you! 🎯
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Browse Deals
            </h1>
            <p className="text-gray-600 mt-1">Find amazing discounts on quality groceries</p>
          </div>
          {cart.length > 0 && (
            <Button
              data-testid="checkout-btn"
              onClick={goToCheckout}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Checkout ({cart.length})
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  data-testid="search-input"
                  placeholder="Search deals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Real-time Connection Status */}
        <div className="flex items-center justify-end mb-2">
          <div className={`flex items-center gap-1.5 text-xs ${isConnected ? 'text-green-600' : 'text-gray-400'}`}>
            {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isConnected ? 'Live updates' : 'Offline'}
          </div>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <ProductCardGrid>
            {[1,2,3,4,5,6,7,8].map(i => <ProductCardSkeleton key={i} />)}
          </ProductCardGrid>
        ) : filteredDeals.length === 0 ? (
          <EmptyProductState />
        ) : (
          <ProductCardGrid>
            {filteredDeals.map((deal) => {
              const inventoryStatus = getItemStatus(deal.id, deal.quantity);
              const hasUpdate = hasRecentUpdate(deal.id);
              
              return (
                <ProductCard
                  key={deal.id}
                  item={deal}
                  inventoryStatus={inventoryStatus}
                  hasRecentUpdate={hasUpdate}
                  onAddToCart={addToCart}
                />
              );
            })}
          </ProductCardGrid>
        )}
      </div>
    </ConsumerLayout>
  );
}
